/* eslint-disable no-useless-escape */
import React, { createContext, useState, useContext, useEffect } from 'react';

import en from '../translations/en.json';
import he from '../translations/he.json';
import es from '../translations/es.json';
import fr from '../translations/fr.json';
import de from '../translations/de.json';
import ru from '../translations/ru.json';
import zh from '../translations/zh.json';
import ar from '../translations/ar.json';

const LanguageContext = createContext();

const translations = { en, he, es, fr, de, ru, zh, ar };

export function LanguageProvider({ children }) {
  const [language, setLanguage] = useState(localStorage.getItem('language') || 'en');
  const [region, setRegion] = useState(localStorage.getItem('region') || 'GLOBAL');
  const [dir, setDir] = useState('ltr');

  useEffect(() => {
    // Check IP on first visit
    if (!localStorage.getItem('ip_checked')) {
      // Primary: ipinfo.io, Fallback: ipapi.co
      fetch('https://ipinfo.io/json')
        .then(res => res.json())
        .catch(() => fetch('https://ipapi.co/json/').then(res => res.json()))
        .then(data => {
          // Check if user manually changed it while we were fetching
          const hasManualLang = localStorage.getItem('manual_lang_set') === 'true';
          const hasManualRegion = localStorage.getItem('manual_region_set') === 'true';
          
          // data.country can be from ipinfo or ipapi
          if (data && data.country === 'IL') {
            if (!hasManualLang) setLanguage('he');
            if (!hasManualRegion) setRegion('IL');
          } else if (data && data.country) {
            if (!hasManualLang) setLanguage('en');
            if (!hasManualRegion) setRegion('GLOBAL');
          }
          localStorage.setItem('ip_checked', 'true');
        })
        .catch(err => console.error('IP fetch error:', err));
    }
  }, []);

  useEffect(() => {
    localStorage.setItem('language', language);
    const rtlLanguages = ['he', 'ar'];
    const newDir = rtlLanguages.includes(language) ? 'rtl' : 'ltr';
    setDir(newDir);
    document.documentElement.dir = newDir;
    document.documentElement.lang = language;
  }, [language]);

  const handleSetLanguage = (newLang) => {
    localStorage.setItem('manual_lang_set', 'true');
    setLanguage(newLang);
  };

  const handleSetRegion = (newRegion) => {
    localStorage.setItem('manual_region_set', 'true');
    localStorage.setItem('region', newRegion);
    setRegion(newRegion);
  };

  useEffect(() => {
    localStorage.setItem('region', region);
  }, [region]);

  const t = (key, defaultValue) => {
    if (!key || typeof key !== 'string') return defaultValue || key;
    const resolve = (path, obj) => path.split('.').reduce((prev, curr) => prev ? prev[curr] : null, obj) 
    
    let translation = resolve(key, translations[language]);
    if (translation === undefined || translation === null) {
        translation = resolve(key, translations.en);
    }
    
    return (translation !== undefined && translation !== null) ? translation : (defaultValue || key);
  };

  return (
    <LanguageContext.Provider value={{ language, setLanguage: handleSetLanguage, region, setRegion: handleSetRegion, dir, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export const useLanguage = () => {
  const context = useContext(LanguageContext);
  if (!context) throw new Error('useLanguage must be used within LanguageProvider');
  return context;
};

export const languages = [
  { code: 'en', name: 'English', flag: '🇺🇸', dir: 'ltr' },
  { code: 'he', name: 'עברית', flag: '🇮🇱', dir: 'rtl' },
  { code: 'es', name: 'Español', flag: '🇪🇸', dir: 'ltr' },
  { code: 'fr', name: 'Français', flag: '🇫🇷', dir: 'ltr' },
  { code: 'de', name: 'Deutsch', flag: '🇩🇪', dir: 'ltr' },
  { code: 'ru', name: 'Русский', flag: '🇷🇺', dir: 'ltr' },
  { code: 'zh', name: '中文', flag: '🇨🇳', dir: 'ltr' },
  { code: 'ar', name: 'العربية', flag: '🇦🇪', dir: 'rtl' }
];
